import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Megaphone, ArrowRight, CheckCircle2, Eye, Target, TrendingUp,
  Sparkles, Monitor, Mail, FileText, Plane, Building2, Users, Globe,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEMENTS = [
  {
    icon: Monitor,
    tag: 'Homepage',
    title: 'Banner Premium Home',
    desc: 'Posizione di massima visibilità sopra la sezione "In Evidenza". Ideale per brand awareness e lancio nuovi modelli.',
    specs: ['1920 × 400 px', 'Desktop + mobile responsive', 'Click-through tracciato'],
  },
  {
    icon: FileText,
    tag: 'Blog',
    title: 'Native Ads & Articoli Sponsorizzati',
    desc: 'Contenuti editoriali realizzati con il nostro team, inseriti nel blog AeroDeals con lettore target di alto profilo.',
    specs: ['Articolo dedicato', 'Backlink dofollow', 'Promozione social'],
  },
  {
    icon: Plane,
    tag: 'Annunci',
    title: 'Listing Highlight & Dealer Page',
    desc: 'Spazio dedicato ai dealer: pagina brand personalizzata, logo sui tuoi annunci e badge "Partner Ufficiale".',
    specs: ['Pagina dealer', 'Badge partner', 'Listing prioritari'],
  },
  {
    icon: Mail,
    tag: 'Newsletter',
    title: 'Sponsorship Newsletter',
    desc: 'Inserzione nella newsletter mensile inviata ai nostri iscritti: piloti, proprietari e operatori dell\'aviazione generale.',
    specs: ['Logo + copy', 'CTA tracciato', 'Report performance'],
  },
];

const AUDIENCE = [
  { icon: Users, kpi: 'Piloti & Proprietari', desc: 'Audience verticale, altamente qualificata.' },
  { icon: Building2, kpi: 'Dealer & Operatori', desc: 'Aziende del settore e flight school.' },
  { icon: Globe, kpi: 'Italia & Europa', desc: 'Copertura sul mercato EU dell\'aviazione generale.' },
  { icon: TrendingUp, kpi: 'Traffico in crescita', desc: 'Marketplace in espansione continua.' },
];

const REASONS = [
  { icon: Target, title: 'Targeting verticale', desc: 'Il 100% degli utenti è interessato al mondo dell\'aviazione. Nessuna dispersione di budget.' },
  { icon: Eye, title: 'Visibilità premium', desc: 'Formati chiari, in linea con il design editoriale del sito. Zero intrusività, massima percezione.' },
  { icon: Sparkles, title: 'Team dedicato', desc: 'Creatività, copy e ottimizzazione in pacchetto. Lanciamo la campagna con te.' },
];

const PACKAGES = [
  { name: 'Essential', tagline: 'Per iniziare a farsi vedere', features: ['1 posizionamento', '30 giorni di durata', 'Report di base'] },
  { name: 'Growth', tagline: 'La scelta più popolare', features: ['3 posizionamenti combinati', '60 giorni di durata', 'Articolo sponsorizzato incluso', 'Report mensile'], popular: true },
  { name: 'Brand', tagline: 'Massima presenza, massimo impatto', features: ['Posizionamenti illimitati', 'Durata annuale', 'Pagina dealer dedicata', 'Account manager dedicato'] },
];

const CATEGORIES = [
  'Dealer / Operatore',
  'Costruttore',
  'Flight school',
  'Assicurazioni aviation',
  'Manutenzione & MRO',
  'Servizi professionali',
  'Altro',
];

export default function SpazioPubblicitario() {
  const [form, setForm] = useState({
    nome: '',
    cognome: '',
    email: '',
    azienda: '',
    categoria: '',
    budget: '',
    messaggio: '',
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'Spazio Pubblicitario & Sponsorship | AeroDeals';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'Promuovi il tuo brand sul marketplace aviation AeroDeals: banner premium in homepage, articoli sponsorizzati, newsletter e pagina dealer. Audience verticale di piloti e operatori.');
  }, []);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const scrollToForm = () => document.getElementById('form-ads')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email || !form.azienda) {
      toast.error('Compila tutti i campi obbligatori.');
      return;
    }
    setLoading(true);
    try {
      const messaggioCompleto = [
        `Azienda: ${form.azienda}`,
        form.categoria ? `Categoria: ${form.categoria}` : null,
        form.budget ? `Budget indicativo: ${form.budget}` : null,
        form.messaggio ? `\n${form.messaggio}` : null,
      ].filter(Boolean).join('\n');
      await axios.post(`${API}/consulenze/request`, {
        nome: form.nome,
        cognome: form.cognome,
        email: form.email,
        oggetto: 'Spazio Pubblicitario',
        messaggio: messaggioCompleto,
      });
      setSent(true);
      toast.success('Richiesta inviata. Ti contatteremo a breve.');
      setForm({ nome: '', cognome: '', email: '', azienda: '', categoria: '', budget: '', messaggio: '' });
    } catch (err) {
      console.error(err);
      toast.error("Errore durante l'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="spazio-pubblicitario-page">
      {/* ─── Hero ─── */}
      <section className="relative py-24 md:py-32 border-b border-zinc-800/50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#C5A059]/[0.08] via-transparent to-transparent pointer-events-none" />
        <div className="absolute right-[-5%] top-[-5%] w-[550px] h-[550px] rounded-full bg-[#C5A059]/[0.05] blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr,1fr] gap-12 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Servizi · Spazio Pubblicitario</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 leading-[1.05]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Promuovi il tuo brand<br /><span className="text-[#C5A059]">nel cielo italiano</span>
              </h1>
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-8 max-w-xl">
                AeroDeals è la vetrina del marketplace aviation in Italia e Europa. Spazi pubblicitari premium per
                raggiungere piloti, proprietari, dealer e operatori dell&apos;aviazione generale.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors"
                  data-testid="cta-hero-ads"
                >
                  Richiedi il Media Kit <ArrowRight size={14} />
                </button>
                <a
                  href="#placements"
                  className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 px-6 py-3 font-medium text-sm hover:border-[#C5A059] hover:text-white transition-colors"
                >
                  Scopri i formati
                </a>
              </div>
              <div className="grid grid-cols-3 gap-4 max-w-lg border-t border-zinc-800 pt-6">
                <div className="flex items-start gap-2.5">
                  <Target size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">100%</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Audience aviation</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Globe size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">Italia & EU</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Copertura mercato</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Sparkles size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">Su misura</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Pacchetti dedicati</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero mock media card */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C5A059]/15 to-transparent blur-2xl pointer-events-none" />
                <div className="relative bg-gradient-to-br from-zinc-900 to-[#0E0E10] border border-zinc-800 p-7">
                  <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#C5A059]/50 to-transparent" />
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C5A059]/15">
                      <Megaphone size={18} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">AeroDeals · Media Kit</div>
                      <div className="text-sm text-zinc-400">Placements disponibili</div>
                    </div>
                  </div>
                  {/* Mock banner */}
                  <div className="aspect-[16/5] bg-gradient-to-br from-[#C5A059]/20 via-zinc-800 to-zinc-900 border border-zinc-800 flex items-center justify-center mb-4 relative overflow-hidden">
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Banner Homepage · 1920×400</span>
                    <div className="absolute top-2 right-2 text-[9px] font-mono text-[#C5A059] bg-black/40 px-2 py-0.5">SPONSORED</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="border border-zinc-800 p-3">
                      <div className="text-[10px] font-mono text-zinc-500 uppercase mb-1">Articolo</div>
                      <div className="space-y-1">
                        <div className="h-1.5 w-3/4 bg-zinc-800" />
                        <div className="h-1.5 w-full bg-zinc-800" />
                      </div>
                    </div>
                    <div className="border border-zinc-800 p-3">
                      <div className="text-[10px] font-mono text-zinc-500 uppercase mb-1">Newsletter</div>
                      <div className="space-y-1">
                        <div className="h-1.5 w-2/3 bg-zinc-800" />
                        <div className="h-1.5 w-5/6 bg-zinc-800" />
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-zinc-800 text-xs text-zinc-500">
                    Formati ottimizzati per brand awareness e lead generation.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Audience ─── */}
      <section className="py-16 md:py-20 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Audience</p>
          <h2 className="text-2xl md:text-3xl tracking-tighter font-medium text-white mb-10 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Un pubblico verticale, qualificato e in crescita
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {AUDIENCE.map((a, i) => (
              <div key={a.kpi} className="bg-zinc-900/40 border border-zinc-800 p-5" data-testid={`audience-${i}`}>
                <a.icon size={18} className="text-[#C5A059] mb-3" />
                <div className="text-base font-medium text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{a.kpi}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Placements ─── */}
      <section id="placements" className="py-20 md:py-24 border-b border-zinc-800/50 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Placements</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-12 max-w-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Quattro formati per coprire l&apos;intera esperienza utente
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PLACEMENTS.map((p) => (
              <div key={p.title} className="bg-zinc-900/40 border border-zinc-800 p-7 hover:border-[#C5A059]/40 transition-colors" data-testid={`placement-${p.tag.toLowerCase()}`}>
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/15">
                    <p.icon size={22} className="text-[#C5A059]" />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-800 text-zinc-300 px-2.5 py-1">{p.tag}</span>
                </div>
                <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{p.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-5">{p.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {p.specs.map((s) => (
                    <span key={s} className="text-[11px] text-zinc-500 border border-zinc-800 px-2.5 py-1">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why us ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Perché AeroDeals</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-12 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Un partner editoriale, non un semplice spazio pubblicitario
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {REASONS.map((r) => (
              <div key={r.title} className="bg-zinc-900/40 border border-zinc-800 p-6 hover:border-[#C5A059]/40 transition-colors">
                <div className="w-11 h-11 flex items-center justify-center bg-[#C5A059]/15 mb-4">
                  <r.icon size={20} className="text-[#C5A059]" />
                </div>
                <h3 className="text-base font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{r.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Packages ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Pacchetti</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-4 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Tre formule, configurabili su misura
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-12 max-w-xl">
            I prezzi variano in base a durata, posizionamento e volumi. Richiedi un preventivo personalizzato.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PACKAGES.map((pkg) => (
              <div
                key={pkg.name}
                className={`relative p-7 transition-colors flex flex-col ${
                  pkg.popular
                    ? 'bg-gradient-to-br from-[#C5A059]/[0.08] to-transparent border-2 border-[#C5A059]/50'
                    : 'bg-zinc-900/40 border border-zinc-800 hover:border-[#C5A059]/40'
                }`}
                data-testid={`package-${pkg.name.toLowerCase()}`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-6 text-[10px] font-mono uppercase tracking-widest bg-[#C5A059] text-black px-3 py-1">Più richiesto</span>
                )}
                <div className="text-xs font-mono uppercase tracking-widest text-[#C5A059] mb-2">{pkg.name}</div>
                <div className="text-sm text-zinc-400 mb-6">{pkg.tagline}</div>
                <ul className="space-y-3 mb-8 flex-1">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <CheckCircle2 size={15} className="text-[#C5A059] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToForm}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                    pkg.popular
                      ? 'bg-[#C5A059] text-black hover:bg-[#D4B271]'
                      : 'border border-zinc-700 text-zinc-300 hover:border-[#C5A059] hover:text-white'
                  }`}
                  data-testid={`cta-package-${pkg.name.toLowerCase()}`}
                >
                  Richiedi preventivo <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Contact form ─── */}
      <section id="form-ads" className="py-20 md:py-24 border-b border-zinc-800/50 bg-gradient-to-b from-transparent to-[#C5A059]/[0.03]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr,1.3fr] gap-10 lg:gap-14 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Contattaci</p>
              <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Raccontaci la tua realtà
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-8">
                Compila il form con i dati della tua azienda e un&apos;idea del budget. Ti ricontatteremo entro 24 ore con
                un piano pubblicitario su misura e il media kit completo.
              </p>
              <ul className="space-y-3 text-sm text-zinc-300">
                {['Risposta entro 24 ore', 'Media kit con tariffe dettagliate', 'Proposta personalizzata', 'Nessun impegno'].map((t) => (
                  <li key={t} className="flex items-center gap-2.5">
                    <CheckCircle2 size={15} className="text-[#C5A059] shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              {sent ? (
                <div className="border border-[#C5A059]/40 bg-[#C5A059]/[0.06] p-10 text-center" data-testid="ads-success">
                  <CheckCircle2 size={42} className="text-[#C5A059] mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Richiesta inviata con successo
                  </h3>
                  <p className="text-sm text-zinc-400 mb-6">
                    Grazie! Il nostro team commerciale ti contatterà a breve con il media kit e una proposta
                    personalizzata.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="text-xs font-mono uppercase tracking-widest text-[#C5A059] hover:text-[#D4B271] transition-colors"
                  >
                    Invia un&apos;altra richiesta
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-zinc-900/60 border border-zinc-800 p-7 md:p-9 space-y-5" data-testid="ads-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Nome *</label>
                      <input type="text" value={form.nome} onChange={update('nome')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="ads-nome" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Cognome *</label>
                      <input type="text" value={form.cognome} onChange={update('cognome')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="ads-cognome" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Email *</label>
                    <input type="email" value={form.email} onChange={update('email')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="ads-email" />
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Azienda *</label>
                    <input type="text" value={form.azienda} onChange={update('azienda')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="ads-azienda" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Categoria</label>
                      <select value={form.categoria} onChange={update('categoria')} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors cursor-pointer" data-testid="ads-categoria">
                        <option value="">Seleziona categoria...</option>
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c} className="bg-zinc-950">{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Budget indicativo</label>
                      <select value={form.budget} onChange={update('budget')} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors cursor-pointer" data-testid="ads-budget">
                        <option value="">Non specificato</option>
                        <option value="< €1.000" className="bg-zinc-950">&lt; €1.000</option>
                        <option value="€1.000 – €5.000" className="bg-zinc-950">€1.000 – €5.000</option>
                        <option value="€5.000 – €15.000" className="bg-zinc-950">€5.000 – €15.000</option>
                        <option value="€15.000+" className="bg-zinc-950">€15.000+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Messaggio</label>
                    <textarea rows={5} value={form.messaggio} onChange={update('messaggio')} placeholder="Obiettivi di campagna, tempistiche, formati preferiti (opzionale)" className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors resize-none" data-testid="ads-messaggio" />
                  </div>

                  <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-testid="ads-submit">
                    {loading ? 'Invio in corso...' : 'Richiedi il Media Kit'}
                    {!loading && <ArrowRight size={14} />}
                  </button>

                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Inviando la richiesta accetti la nostra <a href="/privacy" className="text-[#C5A059] hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Outro ─── */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="border-l-2 border-[#C5A059] pl-6 text-zinc-300 leading-relaxed">
            <strong className="text-white">Scopri tutti i velivoli attualmente disponibili</strong> visitando la nostra pagina{' '}
            <a href="/listings" className="text-[#C5A059] hover:underline">Annunci</a>. La stessa audience vedrà la tua
            campagna: piloti, proprietari e operatori dell&apos;aviazione generale.
          </blockquote>
        </div>
      </section>
    </div>
  );
}
